import type { UUID } from '@/types';

/**
 * TanStack Query key factory.
 * Centralized keys ensure consistent cache invalidation across features.
 */
export const queryKeys = {
  // Session
  session: () => ['session'] as const,

  config: {
    public: ['config', 'public'] as const,
  },

  // Dashboard
  dashboard: {
    role: () => ['dashboard', 'role'] as const,
    all: (tenantId: UUID) => ['dashboard', tenantId] as const,
    summary: (tenantId: UUID) => ['dashboard', tenantId, 'summary'] as const,
    activity: (tenantId: UUID) => ['dashboard', tenantId, 'activity'] as const,
    errors: (tenantId: UUID) => ['dashboard', tenantId, 'errors'] as const,
    healthStrip: (tenantId: UUID) => ['dashboard', tenantId, 'health-strip'] as const,
  },

  me: () => ['users', 'me'] as const,

  loginHistory: (filters?: Record<string, unknown>) =>
    ['login-history', filters ?? {}] as const,

  // Onboarding
  onboarding: {
    state: (tenantId: UUID) => ['onboarding', tenantId, 'state'] as const,
  },

  // Tenants
  tenants: {
    all: () => ['tenants'] as const,
    detail: (tenantId: UUID) => ['tenants', tenantId] as const,
  },

  // Portal users / Agents matrix
  users: {
    all: (tenantId: UUID) => ['users', tenantId] as const,
    detail: (tenantId: UUID, userId: UUID) => ['users', tenantId, userId] as const,
    agentMatrix: (tenantId: UUID) => ['users', tenantId, 'agent-matrix'] as const,
  },

  // Salesforce
  salesforce: {
    orgs: (tenantId: UUID) => ['salesforce-orgs', tenantId] as const,
    org: (tenantId: UUID, orgId: UUID) => ['salesforce-orgs', tenantId, orgId] as const,
    auth: (tenantId: UUID, orgId: UUID) => ['salesforce-orgs', tenantId, orgId, 'auth'] as const,
    routing: (tenantId: UUID, orgId: UUID) =>
      ['salesforce-orgs', tenantId, orgId, 'routing'] as const,
    bindings: (tenantId: UUID, orgId: UUID) =>
      ['salesforce-orgs', tenantId, orgId, 'bindings'] as const,
    discovery: (tenantId: UUID, orgId: UUID, type: string) =>
      ['salesforce-orgs', tenantId, orgId, 'discovery', type] as const,
  },

  // Telephony
  telephony: {
    connections: (tenantId: UUID) => ['telephony-connections', tenantId] as const,
    connection: (tenantId: UUID, connectionId: UUID) =>
      ['telephony-connections', tenantId, connectionId] as const,
    twilio: (tenantId: UUID, connectionId: UUID) =>
      ['telephony-connections', tenantId, connectionId, 'twilio'] as const,
    connect: (tenantId: UUID, connectionId: UUID) =>
      ['telephony-connections', tenantId, connectionId, 'connect'] as const,
    numbers: (tenantId: UUID, connectionId?: UUID) =>
      connectionId
        ? (['phone-numbers', tenantId, connectionId] as const)
        : (['phone-numbers', tenantId] as const),
    webhooks: (tenantId: UUID, connectionId: UUID) =>
      ['telephony-connections', tenantId, connectionId, 'webhooks'] as const,
    providers: () => ['providers'] as const,
    defaults: (tenantId: UUID) => ['telephony-defaults', tenantId] as const,
  },

  // Agents
  agents: {
    all: (tenantId: UUID, filters?: Record<string, unknown>) =>
      ['agents', tenantId, filters ?? {}] as const,
    detail: (tenantId: UUID, agentId: UUID) => ['agents', tenantId, agentId] as const,
  },

  // Validation & health
  validation: {
    summary: (tenantId: UUID, scope?: string, resourceId?: UUID) =>
      ['validation', tenantId, scope ?? 'all', resourceId ?? 'all'] as const,
    checks: (tenantId: UUID, scope?: string, resourceId?: UUID) =>
      ['validation', tenantId, 'checks', scope ?? 'all', resourceId ?? 'all'] as const,
  },

  health: {
    summary: (tenantId: UUID) => ['health', tenantId, 'summary'] as const,
    platform: () => ['health', 'platform'] as const,
  },

  // Calls
  calls: {
    all: (tenantId: UUID, filters?: Record<string, unknown>) =>
      ['calls', tenantId, filters ?? {}] as const,
    detail: (tenantId: UUID, callId: UUID) => ['calls', tenantId, callId] as const,
    events: (tenantId: UUID, callId: UUID) => ['calls', tenantId, callId, 'events'] as const,
  },

  // Audit
  audit: {
    all: (tenantId: UUID, filters?: Record<string, unknown>) =>
      ['audit', tenantId, filters ?? {}] as const,
  },

  // Credentials
  credentials: {
    all: (tenantId: UUID) => ['credentials', tenantId] as const,
    detail: (tenantId: UUID, credentialId: UUID) =>
      ['credentials', tenantId, credentialId] as const,
  },

  // Platform
  platform: {
    tenants: () => ['platform', 'tenants'] as const,
    tenant: (tenantId: UUID) => ['platform', 'tenants', tenantId] as const,
    providers: () => ['platform', 'providers'] as const,
    users: (filters?: Record<string, unknown>) =>
      ['platform', 'users', filters ?? {}] as const,
    dashboard: () => ['platform', 'dashboard'] as const,
    health: () => ['platform', 'health'] as const,
    inventory: () => ['platform', 'inventory'] as const,
  },

  // Search
  search: (tenantId: UUID, query: string) => ['search', tenantId, query] as const,
} as const;
